import { useEffect, useState } from "react";
import { Package } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useCatalogProduct } from "@/hooks/use-wholesale";
import { fmtKm } from "@/lib/wholesale-format";
import { discountForStore, unitPriceOf, type EditorItem } from "@/lib/wholesale-items";
import { cn } from "@/lib/utils";
import { itemKey } from "@/components/wholesale/showroom-grid";
import { Stepper } from "@/components/wholesale/stepper";
import type { ItemSnapshot } from "@/services/wholesale-catalog";

/** Naziv varijacije bez ponovljenog naziva proizvoda: „Haljina Mia - Crna / 42" → „Crna / 42".
 *  Isti račun postoji na serveru (`variationLabel`), ali taj modul povlači serverski kod baze
 *  i ne smije u paket preglednika. */
function shortLabel(productName: string, variationName: string): string {
  const name = (variationName || "").trim();
  const base = (productName || "").trim();
  if (!name) return "";
  const rest = base && name.toLowerCase().startsWith(base.toLowerCase())
    ? name.slice(base.length).replace(/^[\s\-–—:,]+/, "").trim()
    : name;
  return rest || name;
}

const inStock = (it: { stockQuantity: number | null; stockStatus?: string }): boolean =>
  it.stockQuantity === null ? it.stockStatus === "instock" : it.stockQuantity > 0;

/** Detalj artikla u prezentaciji: slika preko cijele širine, varijacije kao mete za prst,
 *  pa količina i dodavanje u korpu. */
export function ShowroomItemSheet({
  item,
  partnerDiscount,
  storeDiscounts,
  quantityInCart,
  onSubmit,
  onClose,
}: {
  item: ItemSnapshot | null;
  partnerDiscount: number;
  storeDiscounts: { storeId: string; discountPct: number }[];
  quantityInCart: (key: string) => number;
  /** `replace` znači da artikal već stoji u korpi i da količina ide na tačno `line.quantity`,
   *  a ne da se zbraja s postojećom. */
  onSubmit: (line: EditorItem, replace: boolean) => void;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useCatalogProduct(item?.storeId, item?.wcProductId);
  const variations = data?.variations ?? [];
  const isVariable = data?.type === "variable" && variations.length > 0;

  const [variationId, setVariationId] = useState<number | null>(null);
  const [qty, setQty] = useState(1);

  // Otvaranje novog artikla vraća izbor na početak: varijacija iz koje se ušlo (kartica
  // izloga zna nositi varijaciju), inače prva raspoloživa.
  useEffect(() => {
    if (!item) return;
    setVariationId(item.wcVariationId);
    setQty(1);
  }, [item?.storeId, item?.wcProductId, item?.wcVariationId]);

  useEffect(() => {
    if (!isVariable || variationId !== null) return;
    setVariationId(variations.find(inStock)?.wcVariationId ?? variations[0]?.wcVariationId ?? null);
  }, [isVariable, variationId, variations]);

  const chosen: ItemSnapshot | null = isVariable
    ? variations.find((v) => v.wcVariationId === variationId) ?? null
    : data ?? null;

  const discountPct = chosen ? discountForStore(chosen.storeId, partnerDiscount, storeDiscounts) : 0;
  const unit = chosen ? unitPriceOf({ listPrice: chosen.listPrice ?? 0, discountPct, overridePrice: null }) : 0;
  const alreadyIn = chosen ? quantityInCart(itemKey(chosen)) : 0;

  // Artikal koji je već u korpi se uređuje, ne dodaje ponovo — stepper zato kreće od
  // količine koja je unutra, a dugme mijenja riječ.
  useEffect(() => {
    if (alreadyIn > 0) setQty(alreadyIn);
  }, [alreadyIn]);

  function handleAdd() {
    if (!chosen) return;
    onSubmit({
      storeId: chosen.storeId, storeName: chosen.storeName, wcProductId: chosen.wcProductId,
      wcVariationId: chosen.wcVariationId, sku: chosen.sku, name: chosen.name, imageUrl: chosen.imageUrl,
      quantity: qty, listPrice: chosen.listPrice ?? 0, discountPct, overridePrice: null,
      stockQuantity: chosen.stockQuantity,
    }, alreadyIn > 0);
    onClose();
  }

  return (
    <Sheet open={!!item} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[88vh] gap-0 p-0">
        <div className="mx-auto flex max-h-[88vh] w-full max-w-3xl flex-col">
          <SheetHeader className="border-b p-4 pb-3 pr-12">
            <SheetTitle className="text-sm leading-snug">{data?.name || item?.name || "Artikal"}</SheetTitle>
            <SheetDescription className="text-[10px] uppercase tracking-[0.08em]">
              {chosen?.sku || item?.sku || "bez SKU"} · {data?.storeName || item?.storeName || ""}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {error ? (
              <p className="p-4 text-xs text-destructive">{(error as Error).message}</p>
            ) : isLoading || !data ? (
              <div className="flex flex-col gap-3 p-4">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <div className="flex flex-col gap-4 p-4 sm:flex-row">
                <div className="relative aspect-square w-full shrink-0 bg-muted ring-1 ring-foreground/10 sm:w-64">
                  {(chosen?.imageUrl || data.imageUrl) ? (
                    <img src={chosen?.imageUrl || data.imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Package className="h-8 w-8 text-muted-foreground/40" />
                    </div>
                  )}
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-semibold tabular-nums leading-none">{fmtKm(unit)}</span>
                    {discountPct > 0 && (
                      <span className="flex items-baseline gap-2">
                        <span className="text-sm tabular-nums text-muted-foreground line-through">
                          {fmtKm(chosen?.listPrice ?? null)}
                        </span>
                        <span className="px-1 text-[10px] uppercase tracking-[0.08em] tabular-nums ring-1 ring-foreground/20">
                          −{discountPct}%
                        </span>
                      </span>
                    )}
                  </div>

                  <p className={cn("text-xs tabular-nums", chosen && !inStock(chosen) ? "text-destructive" : "text-muted-foreground")}>
                    {chosen ? (chosen.stockQuantity === null ? (inStock(chosen) ? "Na zalihi" : "Nedostupno") : `${chosen.stockQuantity} kom na zalihi`) : "—"}
                  </p>

                  {isVariable && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Varijacija</span>
                      <div className="flex flex-wrap gap-1.5">
                        {variations.map((v) => {
                          const available = inStock(v);
                          return (
                            <button
                              key={v.wcVariationId ?? 0}
                              type="button"
                              disabled={!available}
                              onClick={() => setVariationId(v.wcVariationId)}
                              className={cn(
                                "flex h-11 min-w-11 flex-col items-center justify-center border px-3 text-xs transition-colors",
                                v.wcVariationId === variationId
                                  ? "border-foreground bg-foreground font-medium text-background"
                                  : "border-input",
                                !available && "opacity-40",
                              )}
                            >
                              <span>{shortLabel(data.name, v.name) || "—"}</span>
                              <span className="text-[10px] tabular-nums opacity-70">
                                {available ? `${v.stockQuantity ?? ""} kom`.trim() : "nema"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {data.type === "variable" && variations.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Varijacije još nisu zrcaljene — pokreni sinhronizaciju shopa da se prikažu.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 border-t p-3">
            <Stepper value={qty} onChange={(n) => setQty(Math.max(1, n))} />
            <button
              type="button"
              onClick={handleAdd}
              disabled={!chosen}
              className="h-11 flex-1 bg-foreground text-sm font-medium text-background transition-opacity active:opacity-80 disabled:opacity-40"
            >
              {alreadyIn > 0 ? "Sačuvaj količinu" : "Dodaj u korpu"}
              <span className="ml-2 tabular-nums opacity-70">{fmtKm(unit * qty)}</span>
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
